import { Button } from '@cargoflow/ui/components/button'
import { createFileRoute, Link, Outlet, redirect, useNavigate } from '@tanstack/react-router'
import { BarChart3, Building2, LogOut, Mail, ShieldCheck } from 'lucide-react'
import { toast } from 'sonner'
import { useAuth } from '@/context/auth-context'
import { assertAdminOrSignOut } from '@/lib/assert-admin'
import { getSession, signOut } from '@/lib/auth-client'

export const Route = createFileRoute('/_authenticated')({
  beforeLoad: async ({ location }) => {
    const session = await getSession()
    if (!session.data) {
      throw redirect({ to: '/sign-in', search: { redirect: location.href } })
    }
    const admin = await assertAdminOrSignOut()
    if (!admin) {
      throw redirect({ to: '/sign-in', search: { redirect: location.href } })
    }
  },
  component: AuthenticatedLayout,
})

function AuthenticatedLayout() {
  const { admin } = useAuth()
  const navigate = useNavigate()

  const handleSignOut = async () => {
    try {
      await signOut()
      navigate({ to: '/sign-in' })
    } catch (_err) {
      toast.error('Sign out failed')
    }
  }

  return (
    <div className="flex min-h-svh flex-col">
      <header className="bg-card sticky top-0 z-10 border-b">
        <div className="mx-auto flex h-14 max-w-7xl items-center gap-6 px-4">
          <Link to="/companies" className="flex items-center gap-2 font-semibold">
            <ShieldCheck className="h-5 w-5" />
            <span>CargoFlow Admin</span>
          </Link>
          <nav className="flex items-center gap-4 text-sm">
            <Link
              to="/companies"
              className="text-muted-foreground hover:text-foreground flex items-center gap-1.5 [&.active]:text-foreground"
            >
              <Building2 className="h-4 w-4" />
              Companies
            </Link>
            <Link
              to="/insights"
              className="text-muted-foreground hover:text-foreground flex items-center gap-1.5 [&.active]:text-foreground"
            >
              <BarChart3 className="h-4 w-4" />
              Insights
            </Link>
            <Link
              to="/campaigns"
              className="text-muted-foreground hover:text-foreground flex items-center gap-1.5 [&.active]:text-foreground"
            >
              <Mail className="h-4 w-4" />
              Campaigns
            </Link>
          </nav>
          <div className="ml-auto flex items-center gap-3 text-sm">
            {admin && (
              <span className="text-muted-foreground">
                {admin.email}
                <span className="bg-muted ml-2 rounded px-1.5 py-0.5 text-xs uppercase">
                  {admin.admin_type.replace('_', ' ')}
                </span>
              </span>
            )}
            <Button variant="ghost" size="sm" onClick={handleSignOut}>
              <LogOut className="h-4 w-4" />
              Sign out
            </Button>
          </div>
        </div>
      </header>
      <main className="mx-auto w-full max-w-7xl flex-1 p-6">
        <Outlet />
      </main>
    </div>
  )
}
